/**
 * PC Quick View — variant-picker modal for product cards.
 *
 * Opened by func-cart.js when a multi-variant product's quick-add (bag) icon
 * is clicked. Lazy-fetches /products/{handle}.js, renders a scrollable gallery,
 * variant buttons, quantity stepper, short description and an Add to Bag button.
 *
 * Public API: window.PCQuickView.open(handle, { hiddenVariantIds: [...] })
 *
 * Add to Bag reuses the site cart flow: POSTs to theme.routes.cartAdd and
 * dispatches 'ajaxProduct:added', which the theme listens for to open the
 * cart drawer (see theme.js).
 */
(function () {
  'use strict';

  var DESC_MAX = 160;

  var modal = null;
  var els = {};
  var product = null;
  var hiddenIds = [];
  var visibleVariants = [];
  var selectedVariant = null;
  var addedData = null; // cart/add.js response for the last successful add

  // Localized string lookup (theme.strings is populated in layout/theme.liquid
  // via Liquid | t). Falls back to English if a key isn't present.
  function S(key, fallback) {
    return (window.theme && theme.strings && theme.strings[key]) || fallback;
  }

  // ---- Money formatting (mirrors theme money format) -------------------------
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var format =
      (window.theme && window.theme.settings && window.theme.settings.moneyFormat) ||
      '${{amount}}';

    function withDelims(number, precision) {
      precision = precision == null ? 2 : precision;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      var rest = parts[1] ? '.' + parts[1] : '';
      return dollars + rest;
    }

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals':
        value = withDelims(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelims(cents, 2).replace(/\,/g, '.');
        break;
      default:
        value = withDelims(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  // CDN images support a width query param; request a sensible size.
  function sized(url, w) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function stripHtml(html) {
    // Remove HTML comments (e.g. Office/Excel "<!-- td {mso-...} -->" paste junk)
    // from the raw markup before parsing.
    html = (html || '').replace(/<!--[\s\S]*?-->/g, '');
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var text = tmp.textContent || tmp.innerText || '';
    // Also strip any comment markup that survived as literal text (when the source
    // had it HTML-escaped, it decodes into visible "<!-- ... -->" characters).
    text = text.replace(/<!--[\s\S]*?-->/g, '');
    return text.replace(/\s+/g, ' ').trim();
  }

  // ---- Modal construction ----------------------------------------------------
  function buildModal() {
    if (modal) return;

    modal = document.createElement('div');
    modal.className = 'pc-qv';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="pc-qv__overlay" data-qv-close></div>' +
      '<div class="pc-qv__dialog" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="pc-qv__close" aria-label="Close" data-qv-close>' +
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="pc-qv__skeleton" data-qv-skeleton aria-hidden="true">' +
          '<div class="pc-qv__skeleton-gallery"><div class="pc-qv__sk pc-qv__sk--img"></div></div>' +
          '<div class="pc-qv__skeleton-info">' +
            '<div class="pc-qv__sk pc-qv__sk--title"></div>' +
            '<div class="pc-qv__sk pc-qv__sk--title pc-qv__sk--short"></div>' +
            '<div class="pc-qv__sk pc-qv__sk--price"></div>' +
            '<div class="pc-qv__sk pc-qv__sk--text"></div>' +
            '<div class="pc-qv__sk pc-qv__sk--text pc-qv__sk--short"></div>' +
            '<div class="pc-qv__sk-variants">' +
              '<div class="pc-qv__sk pc-qv__sk--pill"></div>' +
              '<div class="pc-qv__sk pc-qv__sk--pill"></div>' +
            '</div>' +
            '<div class="pc-qv__sk pc-qv__sk--action"></div>' +
          '</div>' +
        '</div>' +
        '<div class="pc-qv__body">' +
          '<div class="pc-qv__gallery-wrap">' +
            '<button type="button" class="pc-qv__scroll pc-qv__scroll--up" data-qv-scroll="up" aria-label="Scroll images up">' +
              '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 10l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>' +
            '<div class="pc-qv__gallery" data-qv-gallery></div>' +
            '<button type="button" class="pc-qv__scroll pc-qv__scroll--down" data-qv-scroll="down" aria-label="Scroll images down">' +
              '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="pc-qv__info">' +
            '<h2 class="pc-qv__title" data-qv-title></h2>' +
            '<div class="pc-qv__price" data-qv-price></div>' +
            '<div class="pc-qv__desc" data-qv-desc></div>' +
            '<div class="pc-qv__variants" data-qv-variants></div>' +
            '<div class="pc-qv__actions">' +
              '<div class="pc-qv__qty">' +
                '<button type="button" class="pc-qv__qty-btn" data-qv-qty-minus aria-label="Decrease quantity">&minus;</button>' +
                '<input type="text" class="pc-qv__qty-input" data-qv-qty value="1" inputmode="numeric" aria-label="' + S('quantityLabel', 'Quantity') + '">' +
                '<button type="button" class="pc-qv__qty-btn" data-qv-qty-plus aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="pc-qv__add" data-qv-add>' +
                '<span class="pc-qv__add-text">' + S('addToBag', 'Add to Bag') + '</span>' +
                '<span class="pc-qv__add-spinner"></span>' +
              '</button>' +
            '</div>' +
            // Sold-out variant: Back in Stock (Amp) trigger - class + data-product-data /
            // data-variant-id are the app's contract; its embed opens the popup on click.
            '<button type="button" class="pc-qv__notify BIS_trigger" data-qv-notify>' + S('qvNotifyMe', 'Notify me when available') + '</button>' +
            '<button type="button" class="pc-qv__viewbag" data-qv-viewbag>' + S('qvOpenBag', 'Open Bag') + '</button>' +
            '<a class="pc-qv__view-product" data-qv-view href="#">' + S('qvViewDetails', 'View full product details') + ' &rarr;</a>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);

    els.gallery = modal.querySelector('[data-qv-gallery]');
    els.scrollUp = modal.querySelector('[data-qv-scroll="up"]');
    els.scrollDown = modal.querySelector('[data-qv-scroll="down"]');
    els.title = modal.querySelector('[data-qv-title]');
    els.price = modal.querySelector('[data-qv-price]');
    els.desc = modal.querySelector('[data-qv-desc]');
    els.variants = modal.querySelector('[data-qv-variants]');
    els.qty = modal.querySelector('[data-qv-qty]');
    els.add = modal.querySelector('[data-qv-add]');
    els.viewbag = modal.querySelector('[data-qv-viewbag]');
    els.notify = modal.querySelector('[data-qv-notify]');
    els.view = modal.querySelector('[data-qv-view]');

    // Close interactions
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    // Quantity (changing qty after an add resets the button so they can add again)
    modal.querySelector('[data-qv-qty-minus]').addEventListener('click', function () {
      setQty(getQty() - 1);
      resetAddedState();
    });
    modal.querySelector('[data-qv-qty-plus]').addEventListener('click', function () {
      setQty(getQty() + 1);
      resetAddedState();
    });
    els.qty.addEventListener('change', function () { setQty(getQty()); resetAddedState(); });

    // Variant select
    els.variants.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qv-variant]');
      if (!btn) return;
      var v = visibleVariants[parseInt(btn.getAttribute('data-qv-variant'), 10)];
      if (v) selectVariant(v);
    });

    // Add to bag / Open bag
    els.add.addEventListener('click', addToCart);
    els.viewbag.addEventListener('click', openBag);

    // Notify me: the Back in Stock embed handles the click itself (document-level
    // delegate on .BIS_trigger). Close the quick view right after so its popup is
    // never hidden behind this modal's overlay.
    els.notify.addEventListener('click', function () {
      setTimeout(close, 0);
    });

    // Drag-to-scroll on the gallery (grab + pull up/down)
    enableDragScroll(els.gallery);

    // Gallery scroll arrows — snap to the previous/next image so a whole image
    // is always framed (rather than landing mid-image).
    els.scrollUp.addEventListener('click', function () { scrollToAdjacentImage(-1); });
    els.scrollDown.addEventListener('click', function () { scrollToAdjacentImage(1); });
    els.gallery.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);
  }

  // scrollTop offset of each image within the gallery's scroll content.
  function imageTops() {
    var base = els.gallery.getBoundingClientRect().top - els.gallery.scrollTop;
    return Array.prototype.map.call(
      els.gallery.querySelectorAll('.pc-qv__img'),
      function (el) { return Math.round(el.getBoundingClientRect().top - base); }
    );
  }

  function scrollToAdjacentImage(dir) {
    var tops = imageTops();
    if (!tops.length) return;
    var cur = els.gallery.scrollTop;
    var target;
    if (dir > 0) {
      // first image starting below the current position
      target = null;
      for (var i = 0; i < tops.length; i++) {
        if (tops[i] > cur + 4) { target = tops[i]; break; }
      }
      if (target === null) target = els.gallery.scrollHeight; // already at last → bottom
    } else {
      // last image starting above the current position
      target = 0;
      for (var j = 0; j < tops.length; j++) {
        if (tops[j] < cur - 4) target = tops[j]; else break;
      }
    }
    els.gallery.scrollTo({ top: target, behavior: 'smooth' });
  }

  // Show/hide arrows based on overflow + scroll position.
  function updateArrows() {
    if (!els.gallery || !els.scrollUp || !els.scrollDown) return;
    var el = els.gallery;
    var hasOverflow = el.scrollHeight - el.clientHeight > 8;
    var atTop = el.scrollTop <= 4;
    var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    els.scrollUp.classList.toggle('pc-qv__scroll--hidden', !hasOverflow || atTop);
    els.scrollDown.classList.toggle('pc-qv__scroll--hidden', !hasOverflow || atBottom);
  }

  function enableDragScroll(el) {
    var isDown = false;
    var startY = 0;
    var startScroll = 0;

    el.addEventListener('mousedown', function (e) {
      isDown = true;
      startY = e.pageY;
      startScroll = el.scrollTop;
      el.classList.add('is-dragging');
      e.preventDefault(); // stop image ghost-drag / text selection
    });

    document.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      el.scrollTop = startScroll - (e.pageY - startY);
    });

    function stop() {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
    }
    document.addEventListener('mouseup', stop);
    document.addEventListener('mouseleave', stop);
  }

  function getQty() {
    var n = parseInt(els.qty.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }
  function setQty(n) {
    if (isNaN(n) || n < 1) n = 1;
    els.qty.value = n;
  }

  // ---- Open / fetch / render -------------------------------------------------
  function open(handle, opts) {
    if (!handle) return;
    buildModal();
    hiddenIds = (opts && opts.hiddenVariantIds) || [];
    show();
    setLoading(true);

    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        product = data;
        // Same JSON the app reads from data-product-data on collection cards.
        els.notify.setAttribute('data-product-data', JSON.stringify(data));
        render();
      })
      .catch(function (err) {
        console.error('[PC QuickView]', err);
        // Fall back to the product page so the shopper is never stuck.
        window.location.href = '/products/' + handle;
      });
  }

  function setLoading(isLoading) {
    modal.classList.toggle('is-loading', !!isLoading);
  }

  function render() {
    setLoading(false);

    // Variants minus hidden ones
    visibleVariants = (product.variants || []).filter(function (v) {
      return hiddenIds.indexOf(String(v.id)) === -1;
    });
    if (!visibleVariants.length) visibleVariants = product.variants || [];

    els.title.textContent = product.title;
    els.view.href = product.url || ('/products/' + product.handle);

    // Description (short)
    var desc = stripHtml(product.description);
    if (desc.length > DESC_MAX) desc = desc.slice(0, DESC_MAX).replace(/\s+\S*$/, '') + '…';
    els.desc.textContent = desc;
    els.desc.style.display = desc ? '' : 'none';

    // Gallery
    els.gallery.scrollTop = 0;
    var imgs = product.images && product.images.length ? product.images : [product.featured_image];
    els.gallery.innerHTML = imgs
      .filter(Boolean)
      .map(function (src) {
        return '<div class="pc-qv__img"><img src="' + sized(src, 600) + '" alt="" loading="lazy"></div>';
      })
      .join('');
    // Recompute arrows now and again as images load (heights change).
    updateArrows();
    els.gallery.querySelectorAll('img').forEach(function (img) {
      img.addEventListener('load', updateArrows);
    });
    setTimeout(updateArrows, 400);

    // Variant buttons (hide the lone "Default Title" option)
    var single = visibleVariants.length === 1 && visibleVariants[0].title === 'Default Title';
    els.variants.style.display = single ? 'none' : '';
    if (!single) {
      els.variants.innerHTML = visibleVariants
        .map(function (v, i) {
          var cls = 'pc-qv__variant';
          if (!v.available) cls += ' pc-qv__variant--soldout';
          return '<button type="button" class="' + cls + '" data-qv-variant="' + i + '">' +
            '<span>' + v.title + '</span></button>';
        })
        .join('');
    }

    // Default selection: first available, else first
    var first = null;
    for (var i = 0; i < visibleVariants.length; i++) {
      if (visibleVariants[i].available) { first = visibleVariants[i]; break; }
    }
    selectVariant(first || visibleVariants[0]);
    setQty(1);
  }

  function selectVariant(v) {
    selectedVariant = v;
    resetAddedState();

    // Active button state
    var btns = els.variants.querySelectorAll('[data-qv-variant]');
    btns.forEach(function (b) {
      var bv = visibleVariants[parseInt(b.getAttribute('data-qv-variant'), 10)];
      b.classList.toggle('is-selected', bv && bv.id === v.id);
    });

    // Price (+ compare-at)
    var html = '<span class="pc-qv__price-current">' + formatMoney(v.price) + '</span>';
    if (v.compare_at_price && v.compare_at_price > v.price) {
      html += ' <span class="pc-qv__price-compare">' + formatMoney(v.compare_at_price) + '</span>';
    }
    els.price.innerHTML = html;

    // Scroll gallery to the variant image if it has one
    if (v.featured_image && v.featured_image.src) {
      var target = sized(v.featured_image.src, 600);
      var imgEls = els.gallery.querySelectorAll('img');
      for (var i = 0; i < imgEls.length; i++) {
        if (imgEls[i].src === target) {
          imgEls[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
          break;
        }
      }
    }

    // Add-to-bag availability (+ Notify me when the chosen variant is sold out)
    if (v.available) {
      els.add.disabled = false;
      els.add.classList.remove('pc-qv__add--soldout');
      els.add.querySelector('.pc-qv__add-text').textContent = S('addToBag', 'Add to Bag');
      els.notify.classList.remove('pc-qv__notify--show');
      els.notify.removeAttribute('data-variant-id');
    } else {
      els.add.disabled = true;
      els.add.classList.add('pc-qv__add--soldout');
      els.add.querySelector('.pc-qv__add-text').textContent = S('soldOut', 'Sold Out');
      els.notify.setAttribute('data-variant-id', String(v.id));
      els.notify.classList.add('pc-qv__notify--show');
    }
  }

  // Add silently (no drawer pop). On success the button switches to "Added"
  // and an "Open Bag" button appears for the shopper to open the cart drawer.
  function addToCart() {
    if (!selectedVariant || !selectedVariant.available) return;
    if (els.add.classList.contains('is-loading')) return;
    if (els.add.classList.contains('is-added')) return;

    var variantId = selectedVariant.id;
    var qty = getQty();

    els.add.classList.add('is-loading');

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      body: JSON.stringify({ id: variantId, quantity: qty }),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        addedData = data;
        els.add.classList.remove('is-loading');
        setAddedState();
        refreshCartCount();
        // Rebuild the drawer's contents now (without opening it) so the bag
        // isn't stale if the shopper closes the modal instead of "Open Bag".
        // cart:build = theme's silent CartForm.buildCart(); ajaxProduct:added
        // (used by openBag) is the build + open variant.
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function (err) {
        els.add.classList.remove('is-loading');
        console.error('[PC QuickView] add to cart failed', err);
      });
  }

  function setAddedState() {
    els.add.classList.add('is-added');
    els.add.querySelector('.pc-qv__add-text').textContent = '✓ ' + S('qvAdded', 'Added');
    els.viewbag.classList.add('pc-qv__viewbag--show');
  }

  function resetAddedState() {
    addedData = null;
    els.add.classList.remove('is-added');
    var label = selectedVariant && !selectedVariant.available ? S('soldOut', 'Sold Out') : S('addToBag', 'Add to Bag');
    els.add.querySelector('.pc-qv__add-text').textContent = label;
    els.viewbag.classList.remove('pc-qv__viewbag--show');
  }

  // Close the modal, then open the cart drawer with fresh contents.
  // ajaxProduct:added is the theme's canonical "added → build + open drawer"
  // event (see theme.js); a trigger click is the fallback.
  function openBag() {
    close();
    // Let the modal close before opening the drawer.
    setTimeout(function () {
      if (addedData) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: addedData, addToCartBtn: null }
        }));
      } else {
        var trigger = document.querySelector('.js-drawer-open-cart');
        if (trigger) trigger.click();
      }
    }, 50);
  }

  // Update the header cart count immediately (drawer isn't opened on add).
  function refreshCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('.jsc-cart-qty, .cart-link__bubble-num').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () { /* non-critical */ });
  }

  // ---- Show / hide -----------------------------------------------------------
  function show() {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pc-qv-open');
  }
  function close() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pc-qv-open');
  }

  window.PCQuickView = { open: open };
})();
